import * as React from "react"
import { cn } from "@/lib/utils"

interface AnimatedProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  max?: number
  size?: "sm" | "md" | "lg"
  variant?: "default" | "success" | "warning" | "danger" | "info"
  showLabel?: boolean
  animated?: boolean
  striped?: boolean
}

const sizeStyles = {
  sm: "h-2",
  md: "h-4",
  lg: "h-6",
}

const variantStyles = {
  default: "bg-blue-600",
  success: "bg-green-600",
  warning: "bg-amber-500",
  danger: "bg-red-600",
  info: "bg-cyan-600",
}

const AnimatedProgress = React.forwardRef<HTMLDivElement, AnimatedProgressProps>(
  (
    {
      className,
      value,
      max = 100,
      size = "md",
      variant = "default",
      showLabel = false,
      animated = true,
      striped = false,
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100)

    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        {showLabel && (
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-semibold text-gray-900">{Math.round(percentage)}%</span>
          </div>
        )}
        <div className={cn("w-full bg-gray-200 rounded-full overflow-hidden", sizeStyles[size])}>
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500 ease-out",
              variantStyles[variant],
              animated && "animate-pulse",
              striped && "bg-[length:20px_20px] bg-[linear-gradient(45deg,rgba(255,255,255,.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,.15)_50%,rgba(255,255,255,.15)_75%,transparent_75%,transparent)]"
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    )
  }
)
AnimatedProgress.displayName = "AnimatedProgress"

export { AnimatedProgress }
